<template>
  <div class="bg-white rounded-sm m-2">
    <!-- Filters & Search Header -->
    <b-row class="mt-2">
      <b-col cols="12">
        <b-row>
          <b-col cols="3">
            <b-form-group label="Search" label-for="search-input" class="ml-2">
              <b-form-input
                id="search-input"
                class="text-gray text-sm noFocus"
                style="padding:6px 5px;"
                v-model="search"
                type="text"
                placeholder="Search by Tracking No."
                v-on:keyup.enter="onSearchEnter">
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="3">
            <b-form-group label="Filter Status" label-for="filter-status" class="ml-2">
              <b-form-select
                id="filter-status"
                v-model="filterStatus"
                v-on:change="fetch"
                class="form-select noFocus text-sm text-gray">
                <b-select-option :value="null">--Filter by Status--</b-select-option>
                <b-select-option value="Pending">Submitted</b-select-option>
                <b-select-option value="Approved">Approved</b-select-option>
                <b-select-option value="Queried">Issues Raised</b-select-option>
                <b-select-option value="PAID">Paid</b-select-option>
                <b-select-option value="PAYMENT_FAILED">Payment Failed</b-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-row>
              <b-col class="mx-auto">
                <p class="paginate-details text-center text-xs font-bold float-right mt-4">
                  <b-form-select v-model="limit" class="w-full text-xs form-select border-0 paginate-details" v-on:change="fetch">
                    <b-select-option value="10">10</b-select-option>
                    <b-select-option value="20">20</b-select-option>
                    <b-select-option value="50">50</b-select-option>
                    <b-select-option value="100">100</b-select-option>
                  </b-form-select>
                  <span>{{((currentPage - 1) * limit) + 1}}</span> -
                  <span v-if="limit > searches.length">{{pagination.totalElements}}</span>
                  <span v-else>{{currentPage * limit}}</span>
                  of {{pagination.totalElements}}
                </p>
              </b-col>
            </b-row>
          </b-col>
        </b-row>

        <!-- Progress bar -->
        <div class="mx-auto">
          <b-progress value="100" v-if="loading" variant="#007ac5" height="0.2rem" animated></b-progress>
        </div>

        <!-- Table -->
        <b-row>
          <b-col cols="12">
            <table class="min-w-full leading-normal">
              <thead>
                <tr class="border-b border-t border-grey">
                  <th class="t-header">Tracking No.</th>
                  <th class="t-header" style="max-width: 300px;">Search Reference</th>
                  <th class="t-header">Document Type</th>
                  <th class="t-header">Payment Mode</th>
                  <th class="t-header text-center">Status</th>
                  <th class="t-header text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr :class="[(idx % 2) ? rows.even : rows.odd]" v-for="(legalDocsSearch, idx) in searches" :key="idx">
                  <td class="t-body text-blue font-bold cursor-pointer hover:underline" @click="viewLegalDocument(legalDocsSearch)">
                    {{ legalDocsSearch.trackingNumber }}
                  </td>
                  <td class="t-body font-bold text-break" style="max-width: 300px; word-break: break-word; white-space: normal;">{{ legalDocsSearch.search || '-' }}</td>
                  <td class="t-body font-bold">{{ cleanEntityType(legalDocsSearch.type || '-') }}</td>
                  <td class="t-body font-bold">
                    {{ legalDocsSearch.paymentMode ? legalDocsSearch.paymentMode.replace(/_/g, ' ') : "-" }}
                  </td>
                  <td class="t-body text-center">
                    <p class="text-xs px-2 py-1 text-capitalize text-center" style="background: #f1f1f1">
                      <span :class="statusColorCode(legalDocsSearch.status ? legalDocsSearch.status.toUpperCase() : '')">
                        {{ statusDataInfo(legalDocsSearch.status ? legalDocsSearch.status.toUpperCase() : '') }}
                      </span>
                    </p>
                  </td>
                  <td class="t-body text-center">
                    <button
                      v-if="legalDocsSearch.status && legalDocsSearch.status.toUpperCase() === 'APPROVED'"
                      type="button"
                      class="bg-green text-white px-2 py-1 rounded-sm text-xs"
                      @click="viewLegalDocument(legalDocsSearch)"
                    >
                      View
                    </button>
                    <button
                      v-else-if="legalDocsSearch.status && (legalDocsSearch.status.toUpperCase() === 'PAID' || legalDocsSearch.status.toUpperCase() === 'PENDING')"
                      type="button"
                      class="bg-blue text-white px-2 py-1 rounded-sm text-xs"
                      @click="viewLegalDocument(legalDocsSearch)"
                    >
                      View Results
                    </button>
                    <span v-else class="text-xs text-gray">No Action</span>
                  </td>
                </tr>
                <tr v-if="searches.length === 0 && !loading">
                  <td colspan="6" class="bg-inertgrey text-center py-2">
                    <span class="text-sm">No results found</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>

        <!-- Pagination -->
        <b-row class="mx-1 my-2" v-if="pagination.totalPages > 1">
          <b-col cols="1">
            <button @click="previous" :class="(currentPage > 1) ? 'paginate-btn' : 'inert-btn'">Previous</button>
          </b-col>
          <b-col cols="1">
            <b-row>
              <b-col cols="12">
                <p class="paginate-details text-center">
                  <b-badge class="bg-blue text-white">
                    {{currentPage}}/{{pagination.totalPages}}
                  </b-badge>
                </p>
              </b-col>
            </b-row>
          </b-col>
          <b-col cols="1">
            <button @click="next" :class="(currentPage < pagination.totalPages) ? 'paginate-btn' : 'inert-btn'">Next</button>
          </b-col>
          <b-col cols="7"></b-col>
          <b-col cols="2">
            <p class="paginate-details text-center text-xs font-bold">
              <span>{{((currentPage - 1) * limit) + 1}}</span> -
              <span v-if="limit > searches.length">{{pagination.totalElements}}</span>
              <span v-else>{{currentPage * limit}}</span>
              of {{pagination.totalElements}}
            </p>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { api } from "@/app/services/api.service";
import router from "@/router";

export default {
  name: "LegalDocumentSearchApplications",
  data() {
    return {
      loading: false,
      searches: [],
      currentPage: 1,
      limit: 10,
      pagination: {
        totalElements: 0,
        totalPages: 0,
      },
      filters: {
        trackingNumber: null,
        referenceNo: null,
        page: 1,
        perPage: 10,
        status: null,
        search: null,
      },
      search: null,
      filterStatus: null,
      statuses: "Pending,Approved,Queried,Escalated,Draft,Denied,AWAITING_PAYMENT_CONFIRMATION,PAID,PAYMENT_FAILED",
      rows: {
        odd: "border-t border-grey hover:bg-header_grey",
        even: "border-t border-grey hover:bg-header_grey",
      },
    };
  },
  mounted() {
    this.fetch();
  },
  methods: {
    fetch() {
      this.filters.page = this.currentPage;
      this.filters.perPage = this.limit;
      if (this.filterStatus) {
        this.filters.status = this.filterStatus;
      } else {
        this.filters.status = this.statuses;
      }
      if (this.search && this.search.trim() !== "") {
        this.filters.search = this.search.trim();
      } else {
        this.filters.search = null;
      }
      this.loading = true;
      this.fetchLegalDocumentPayment(this.filters);
    },
    onSearchEnter() {
      this.currentPage = 1;
      this.fetch();
    },
    async fetchLegalDocumentPayment(data) {
      let suffix = "";
      suffix += data.trackingNumber ? (suffix === "" ? "?trackingNumber=" + data.trackingNumber : "&trackingNumber=" + data.trackingNumber) : "";
      suffix += data.referenceNo ? (suffix === "" ? "?referenceNo=" + data.referenceNo : "&referenceNo=" + data.referenceNo) : "";
      suffix += data.page ? (suffix === "" ? "?page=" + data.page : "&page=" + data.page) : "";
      suffix += data.perPage ? (suffix === "" ? "?perPage=" + data.perPage : "&perPage=" + data.perPage) : "";
      suffix += data.status ? (suffix === "" ? "?status=" + data.status : "&status=" + data.status) : "";
      if (data.search) {
        suffix += (suffix === "" ? "?search=" : "&search=") + encodeURIComponent(data.search);
      }

      try {
        const response = await api().get("legal-documents/search/results" + suffix);
        if (response) {
          this.loading = false;
          const responseData = response.data;
          this.searches = responseData.content || [];
          this.pagination = {
            totalPages: responseData.totalPages || 0,
            totalElements: responseData.totalElements || 0,
          };
        }
      } catch (error) {
        this.loading = false;
        if (error.response && error.response.data && error.response.data.message) {
          this.warningToast("Warning", error.response.data.message);
        }
      }
    },
    next() {
      if (this.currentPage < this.pagination.totalPages) {
        this.currentPage++;
        this.fetch();
      }
    },
    previous() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetch();
      } else {
        this.warningToast("Observation", "No more data to show");
      }
    },
    statusDataInfo(legalDocsSearch) {
      if (!legalDocsSearch) return "";
      const status = legalDocsSearch.toUpperCase();
      if (status === "APPROVED") return "Approved";
      if (status === "ASSIGNED") return "Being Processed";
      if (status === "QUERIED") return "Issues Raised";
      if (status === "ESCALATED") return "Being Processed";
      if (status === "PENDING") return "Submitted";
      if (status === "AWAITING_PAYMENT_CONFIRMATION") return "Confirming Payment";
      if (status === "FAILED" || status === "PAYMENT_FAILED") return "Payment Failed";
      if (status === "PAYMENT_CANCELLED") return "Payment Cancelled";
      if (status === "REJECTED") return "Rejected";
      if (status === "AWAITING_SEARCH_RESULTS") return "Loading Search Results";
      if (status === "DRAFT") return "Draft";
      if (status === "PAID") return "Paid";
      return legalDocsSearch;
    },
    statusColorCode(legalDocsSearch) {
      if (!legalDocsSearch) return "text-gray";
      const status = legalDocsSearch.toUpperCase();
      if (status === "APPROVED" || status === "PAID") return "text-green font-bold";
      if (status === "ASSIGNED" || status === "ESCALATED") return "text-blue font-bold";
      if (status === "QUERIED") return "text-orange font-bold";
      if (status === "PENDING") return "text-gray font-bold";
      if (status === "AWAITING_PAYMENT_CONFIRMATION") return "text-purple font-bold";
      if (status === "FAILED" || status === "PAYMENT_FAILED" || status === "REJECTED") return "text-red font-bold";
      return "text-gray font-bold";
    },
    cleanEntityType(name) {
      if (!name) return "";
      const n = name.replaceAll("-", " ").replaceAll("_", " ");
      return n.charAt(0).toUpperCase() + n.slice(1);
    },
    viewLegalDocument(legalDocsSearch) {
      if (legalDocsSearch && legalDocsSearch.trackingNumber) {
        router.push({ path: "/home/legal-documents/search", query: { id: legalDocsSearch.trackingNumber } });
      }
    },
  },
  watch: {
    search() {
      this.currentPage = 1;
      this.fetch();
    },
  },
};
</script>

<style scoped>
@import "../../../../../assets/styles/table.css";

.paginate-details {
  @apply px-3 py-2 bg-white my-2 text-sm text-gray;
}

.paginate-btn {
  @apply px-3 py-2 bg-white border border-white my-2 text-sm text-blue w-full;
}

.paginate-btn:hover {
  @apply border border-blue text-blue rounded-sm;
}

.inert-btn {
  @apply px-3 py-2 bg-white my-2 text-sm text-grey w-full;
}
</style>
